import { useState } from 'react';
import type { StatusChange } from '../hooks/useBackupData';
import type { Status } from '../types';
import { StatusPill } from './StatusPill';

/**
 * The audit trail. Collectors write here ONLY when a status actually changes,
 * so this is a genuine timeline rather than 15-minute noise - which is what
 * makes "when did this start failing?" answerable.
 */
/**
 * How many transitions fit on a screen before the list stops being readable.
 *
 * Not a performance number - React renders a thousand rows without complaint.
 * It is a comprehension number: past about fifty rows nobody is reading, they
 * are scrolling, and a page of things scrolled past is a page that did its job
 * for nobody.
 */
const PER_PAGE = 50;

export function HistoryTable({ changes }: { changes: StatusChange[] }) {
  const [page, setPage] = useState(0);

  // A quiet estate that suddenly gets noisy would otherwise leave you stranded
  // on a page that no longer exists, staring at an empty table.
  const pages = Math.max(1, Math.ceil(changes.length / PER_PAGE));
  const current = Math.min(page, pages - 1);
  const rows = changes.slice(current * PER_PAGE, current * PER_PAGE + PER_PAGE);

  if (changes.length === 0) {
    return <div className="empty">No status changes recorded yet.</div>;
  }

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Machine / Client</th>
            <th>Change</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td className="mono dim">{when(c.at)}</td>
              <td>
                <div className="cell-name">{c.name}</div>
                <div className="cell-sub">{c.client || 'No client assigned'}</div>
              </td>
              <td className="change-cell">
                <StatusPill status={c.from as Status} />
                <span className="change-arrow" aria-hidden="true">→</span>
                <StatusPill status={c.to as Status} />
              </td>
              <td className="cell-reason" title={c.reason}>{c.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Only when there is more than one page. A pager on a single page of
          results is chrome that says "there is more" when there is not. */}
      {pages > 1 && (
        <div className="pager">
          <button className="btn-link" disabled={current === 0}
                  onClick={() => setPage(current - 1)}>Newer</button>
          <span className="dim">
            {current * PER_PAGE + 1}&ndash;{Math.min((current + 1) * PER_PAGE, changes.length)}
            {' of '}{changes.length}
          </span>
          <button className="btn-link" disabled={current >= pages - 1}
                  onClick={() => setPage(current + 1)}>Older</button>
        </div>
      )}
    </div>
  );
}

function when(ts: { toDate(): Date } | null): string {
  if (!ts?.toDate) return '--';
  return ts.toDate().toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}
